import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { IDeliverableQuestion, IDeliverableAnswer, IDeliverableQuestionAnswer, IUser, IELearningData } from '@shared';
import { SubSink } from 'subsink';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BackOfficeService } from 'src/app/modules/back-office/back-office.service';
import { FirebaseService } from 'src/app/firebase/firebase.service';

@Component({
  selector: 'app-deliverable',
  templateUrl: './deliverable.component.html',
  styleUrls: ['./deliverable.component.scss']
})
export class DeliverableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() deliverableQuestions: IDeliverableQuestion[];
  @Input() masterclassId: string;
  @Input() moduleId: string;
  @Input() chapterId: string;
  @Input() courseId: string;
  @Input() isSelectedCourseLast: boolean = false;
  @Output() goToNextCourse = new EventEmitter();
  @Output() courseCompleted = new EventEmitter();

  deliverableForm: FormGroup;
  isSubmitted: boolean = false;
  isLoading: boolean = true;
  errorMessage: string = '';
  currentUser: IUser;
  existingAnswers: IDeliverableAnswer | null = null;
  saveStatus: 'idle' | 'saving' | 'saved' = 'idle';
  uploadingByQuestion: { [index: number]: boolean } = {};
  uploadErrorByQuestion: { [index: number]: string } = {};

  private autoSaveEnabled: boolean = false;
  subs = new SubSink();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private backOfficeService: BackOfficeService,
    public firebaseService: FirebaseService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.setupAutoSave();
    this.loadCurrentUser();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.deliverableQuestions && !changes.deliverableQuestions.firstChange) {
      this.isSubmitted = false;
      this.isLoading = true;
      this.existingAnswers = null;
      this.autoSaveEnabled = false;
      this.saveStatus = 'idle';
      this.createForm();
      this.setupAutoSave();
      this.loadExistingAnswers();
    }
  }

  private setupAutoSave(): void {
    this.subs.sink = this.deliverableForm.valueChanges
      .pipe(debounceTime(1500), distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(() => {
        if (!this.autoSaveEnabled || this.isLoading || this.isSubmitted || !this.currentUser) return;
        this.autoSaveDraft();
      });
  }

  loadCurrentUser(): void {
    this.subs.sink = this.firebaseService.getCurrentUserProperties().subscribe((user) => {
      this.currentUser = user;
      this.loadExistingAnswers();
    });
  }

  loadExistingAnswers(): void {
    this.isLoading = true;
    this.subs.sink = this.firebaseService.getELearningData().subscribe((data) => {
      if (data?.deliverableAnswers) {
        this.existingAnswers = data.deliverableAnswers.find(
          (answer) => answer.moduleId === this.moduleId
        ) || null;

        if (this.existingAnswers) {
          this.prefillFormWithAnswers(this.existingAnswers.answers);

          if (!this.existingAnswers.isDraft) {
            this.isSubmitted = true;
            this.disableForm();
          }
        }
      }
      this.isLoading = false;
      this.autoSaveEnabled = !this.isSubmitted;
    });
  }

  createForm(): void {
    this.deliverableForm = this.fb.group({});

    this.deliverableQuestions.forEach((question, index) => {
      switch (question.questionType) {
        case 'freeText':
          this.deliverableForm.addControl(
            `Q${index}`,
            this.fb.control('', question.mandatory ? Validators.required : null)
          );
          break;

        case 'fillInTheBlank':
          const blankCount = (question.templateText?.match(/\{\{\d+\}\}/g) || []).length;
          const blanksArray = this.fb.array(
            Array(blankCount).fill('').map(() => this.fb.control('', question.mandatory ? Validators.required : null))
          );
          this.deliverableForm.addControl(`Q${index}`, blanksArray);
          break;

        case 'table':
          const tableGroup = this.fb.group({});
          (question.tableColumns || []).forEach((col, colIndex) => {
            if (question.firstColumnAsNumbering && colIndex === 0) return;
            const rowCount = question.tableRowLabels?.length || question.tableMinRows || 1;
            tableGroup.addControl(
              col.label,
              this.fb.array(Array(rowCount).fill('').map(() => this.fb.control('')))
            );
          });
          this.deliverableForm.addControl(`Q${index}`, tableGroup);
          break;

        case 'imageUpload':
          this.deliverableForm.addControl(`Q${index}`, this.fb.group({
            imageUrl: [''],
            useAiGeneration: [question.allowAiGeneration || false]
          }));
          break;
      }
    });
  }

  prefillFormWithAnswers(answers: IDeliverableQuestionAnswer[]): void {
    const silent = { emitEvent: false };
    answers.forEach((answer, index) => {
      const control = this.deliverableForm.get(`Q${index}`);
      if (!control) return;

      switch (answer.questionType) {
        case 'freeText':
          control.setValue(answer.textAnswer || '', silent);
          break;

        case 'fillInTheBlank':
          if (answer.blanksAnswers) {
            answer.blanksAnswers.forEach((blank, i) => {
              const blankControl = (control as FormArray).at(i);
              if (blankControl) blankControl.setValue(blank, silent);
            });
          }
          break;

        case 'table':
          if (answer.tableAnswers) {
            const question = this.deliverableQuestions[index];
            const tableGroup = control as FormGroup;

            const firstColValues = Object.values(answer.tableAnswers)[0] as string[];
            const savedRowCount = firstColValues?.length || 0;

            (question.tableColumns || []).forEach((col, colIndex) => {
              if (question.firstColumnAsNumbering && colIndex === 0) return;
              const colArray = tableGroup.get(col.label) as FormArray;
              if (colArray) {
                const savedValues = answer.tableAnswers![col.label] || [];

                while (colArray.length < savedRowCount) {
                  colArray.push(this.fb.control(''), silent);
                }

                savedValues.forEach((value, i) => {
                  if (colArray.at(i)) colArray.at(i).setValue(value, silent);
                });
              }
            });
          }
          break;

        case 'imageUpload':
          control.patchValue({
            imageUrl: answer.imageUrl || '',
            useAiGeneration: answer.useAiGeneration || false
          }, silent);
          break;
      }
    });
  }

  getBlankParts(templateText: string): string[] {
    if (!templateText) return [''];
    return templateText.split(/\{\{\d+\}\}/) || [''];
  }

  getBlankCount(templateText: string): number {
    if (!templateText) return 0;
    return (templateText.match(/\{\{\d+\}\}/g) || []).length;
  }

  getBlankControl(questionIndex: number, blankIndex: number): any {
    const formArray = this.deliverableForm.get(`Q${questionIndex}`) as FormArray;
    if (!formArray || blankIndex >= formArray.length) {
      return this.fb.control('');
    }
    return formArray.at(blankIndex);
  }

  getTableCellControl(questionIndex: number, colLabel: string, rowIndex: number): any {
    const tableGroup = this.deliverableForm.get(`Q${questionIndex}`) as FormGroup;
    const colArray = tableGroup?.get(colLabel) as FormArray;
    return colArray?.at(rowIndex);
  }

  getTableRows(questionIndex: number): number[] {
    const question = this.deliverableQuestions[questionIndex];
    if (question.tableRowLabels?.length) {
      return Array(question.tableRowLabels.length).fill(0).map((_, i) => i);
    }
    const tableGroup = this.deliverableForm.get(`Q${questionIndex}`) as FormGroup;
    const colIndex = question.firstColumnAsNumbering ? 1 : 0;
    const firstCol = question.tableColumns?.[colIndex]?.label;
    if (!firstCol) return [0];
    const colArray = tableGroup?.get(firstCol) as FormArray;
    return Array(colArray?.length || 1).fill(0).map((_, i) => i);
  }

  addTableRow(questionIndex: number): void {
    const question = this.deliverableQuestions[questionIndex];
    const tableGroup = this.deliverableForm.get(`Q${questionIndex}`) as FormGroup;

    (question.tableColumns || []).forEach((col, colIndex) => {
      if (question.firstColumnAsNumbering && colIndex === 0) return;
      const colArray = tableGroup.get(col.label) as FormArray;
      colArray.push(this.fb.control(''));
    });

    this.cdRef.detectChanges();
  }

  removeTableRow(questionIndex: number, rowIndex: number): void {
    const question = this.deliverableQuestions[questionIndex];
    const tableGroup = this.deliverableForm.get(`Q${questionIndex}`) as FormGroup;

    (question.tableColumns || []).forEach((col, colIndex) => {
      if (question.firstColumnAsNumbering && colIndex === 0) return;
      const colArray = tableGroup.get(col.label) as FormArray;
      if (colArray.length > (question.tableMinRows || 1)) {
        colArray.removeAt(rowIndex);
      }
    });

    this.cdRef.detectChanges();
  }

  canAddRow(questionIndex: number): boolean {
    const question = this.deliverableQuestions[questionIndex];
    if (!question.tableMaxRows) return true;
    const tableGroup = this.deliverableForm.get(`Q${questionIndex}`) as FormGroup;
    const colIndex = question.firstColumnAsNumbering ? 1 : 0;
    const firstCol = question.tableColumns?.[colIndex]?.label;
    if (!firstCol) return false;
    const colArray = tableGroup.get(firstCol) as FormArray;
    return colArray.length < question.tableMaxRows;
  }

  canRemoveRow(questionIndex: number): boolean {
    const question = this.deliverableQuestions[questionIndex];
    const tableGroup = this.deliverableForm.get(`Q${questionIndex}`) as FormGroup;
    const colIndex = question.firstColumnAsNumbering ? 1 : 0;
    const firstCol = question.tableColumns?.[colIndex]?.label;
    if (!firstCol) return false;
    const colArray = tableGroup.get(firstCol) as FormArray;
    return colArray.length > (question.tableMinRows || 1);
  }

  getTableRowCount(questionIndex: number): number {
    const question = this.deliverableQuestions[questionIndex];
    const tableGroup = this.deliverableForm.get(`Q${questionIndex}`) as FormGroup;
    const colIndex = question.firstColumnAsNumbering ? 1 : 0;
    const firstCol = question.tableColumns?.[colIndex]?.label;
    if (!firstCol) return 0;
    const colArray = tableGroup.get(firstCol) as FormArray;
    return colArray.length;
  }

  onSubmit(): void {
    this.errorMessage = '';

    if (!this.checkMandatoryQuestions()) {
      this.errorMessage = "Veuillez répondre à toutes les questions obligatoires";
      return;
    }

    this.autoSaveEnabled = false;
    this.isSubmitted = true;
    this.disableForm();
    const answers = this.buildAnswers();
    this.saveAnswers(answers, false);
  }

  checkMandatoryQuestions(): boolean {
    for (let i = 0; i < this.deliverableQuestions.length; i++) {
      const question = this.deliverableQuestions[i];
      if (!question.mandatory) continue;

      const control = this.deliverableForm.get(`Q${i}`);
      if (!control) return false;

      switch (question.questionType) {
        case 'freeText':
          if (!control.value || control.value.trim() === '') return false;
          break;

        case 'fillInTheBlank':
          const blanks = (control as FormArray).controls;
          if (blanks.some(b => !b.value || b.value.trim() === '')) return false;
          break;

        case 'table':
          const tableGroup = control as FormGroup;
          const allCols = question.tableColumns || [];
          const cols = question.firstColumnAsNumbering ? allCols.slice(1) : allCols;
          if (cols.length === 0) return false;

          const firstColArray = tableGroup.get(cols[0].label) as FormArray;
          const rowCount = firstColArray?.length || 0;

          let hasAtLeastOneCompleteRow = false;
          for (let r = 0; r < rowCount; r++) {
            const rowIsComplete = cols.every(col => {
              const colArray = tableGroup.get(col.label) as FormArray;
              const value = colArray?.at(r)?.value;
              return value && value.trim() !== '';
            });
            if (rowIsComplete) {
              hasAtLeastOneCompleteRow = true;
              break;
            }
          }
          if (!hasAtLeastOneCompleteRow) return false;
          break;

        case 'imageUpload':
          const imageData = control.value;
          if (!imageData.imageUrl && !imageData.useAiGeneration) return false;
          break;
      }
    }
    return true;
  }

  buildAnswers(): IDeliverableQuestionAnswer[] {
    return this.deliverableQuestions.map((question, index) => {
      const control = this.deliverableForm.get(`Q${index}`);
      const answer: IDeliverableQuestionAnswer = {
        questionId: question.id,
        questionType: question.questionType
      };

      switch (question.questionType) {
        case 'freeText':
          answer.textAnswer = control?.value || '';
          break;

        case 'fillInTheBlank':
          answer.blanksAnswers = (control as FormArray)?.controls.map(c => c.value) || [];
          break;

        case 'table':
          answer.tableAnswers = {};
          const tableGroup = control as FormGroup;
          const allColumns = question.tableColumns || [];
          const inputColumns = question.firstColumnAsNumbering ? allColumns.slice(1) : allColumns;

          const columnArrays: { [key: string]: string[] } = {};
          inputColumns.forEach((col) => {
            const colArray = tableGroup.get(col.label) as FormArray;
            columnArrays[col.label] = colArray?.controls.map(c => c.value) || [];
          });

          const rowCount = inputColumns.length > 0 ? (columnArrays[inputColumns[0].label]?.length || 0) : 0;

          const nonEmptyRowIndices: number[] = [];
          for (let i = 0; i < rowCount; i++) {
            const isRowEmpty = inputColumns.every(col => {
              const value = columnArrays[col.label][i];
              return !value || value.trim() === '';
            });
            if (!isRowEmpty) {
              nonEmptyRowIndices.push(i);
            }
          }
          if (question.firstColumnAsNumbering && allColumns.length > 0) {
            answer.tableAnswers![allColumns[0].label] = nonEmptyRowIndices.map((_, idx) => String(idx + 1));
          }
          inputColumns.forEach((col) => {
            answer.tableAnswers![col.label] = nonEmptyRowIndices.map(i => columnArrays[col.label][i]);
          });
          break;

        case 'imageUpload':
          const imageData = control?.value || {};
          answer.imageUrl = imageData.imageUrl;
          answer.useAiGeneration = imageData.useAiGeneration;
          break;
      }

      return answer;
    });
  }

  saveAnswers(answers: IDeliverableQuestionAnswer[], isDraft: boolean = false): void {
    const deliverableAnswer: IDeliverableAnswer = {
      moduleId: this.moduleId,
      chapterId: this.chapterId,
      courseId: this.courseId,
      answers,
      answeredAt: new Date(),
      updatedAt: this.existingAnswers ? new Date() : undefined,
      isDraft
    };

    this.subs.sink = this.firebaseService.getELearningData().subscribe((data) => {
      let newELearningData: IELearningData = { ...data };

      if (!newELearningData.deliverableAnswers) {
        newELearningData.deliverableAnswers = [];
      }

      const existingIndex = newELearningData.deliverableAnswers.findIndex(
        (a) => a.moduleId === this.moduleId
      );

      if (existingIndex >= 0) {
        newELearningData.deliverableAnswers[existingIndex] = deliverableAnswer;
      } else {
        newELearningData.deliverableAnswers.push(deliverableAnswer);
      }

      this.subs.sink = this.backOfficeService
        .updateELearningDataForUser(this.currentUser.id, newELearningData)
        .subscribe({
          next: () => {
            this.existingAnswers = deliverableAnswer;
            if (isDraft) {
              this.saveStatus = 'saved';
              return;
            }
            this.firebaseService.registerELearningProgress(this.courseId).then(() => {
              this.courseCompleted.emit();
            });
          },
          error: (err) => {
            console.error('Erreur lors de la sauvegarde des réponses:', err);
            if (isDraft) {
              this.saveStatus = 'idle';
            }
          }
        });
    });
  }

  private autoSaveDraft(): void {
    this.saveStatus = 'saving';
    const answers = this.buildAnswers();
    this.saveAnswers(answers, true);
  }

  triggerFileInput(questionIndex: number): void {
    const fileInput = document.getElementById(`fileInput-${questionIndex}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onImageUpload(event: Event, questionIndex: number): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const control = this.deliverableForm.get(`Q${questionIndex}`);
    if (!control) return;

    this.uploadingByQuestion[questionIndex] = true;
    this.uploadErrorByQuestion[questionIndex] = '';

    const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
    const path = `deliverables/${this.currentUser.id}/${Date.now()}_${safeName}`;
    console.log('[deliverable upload] start', { path, size: file.size, type: file.type });

    const storageRef = this.firebaseService.storage.ref(path);
    const uploadTask = storageRef.put(file);

    uploadTask.snapshotChanges().subscribe({
      next: (snapshot) => {
        if (snapshot) {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          console.log('[deliverable upload] progress', progress + '%');
        }
      },
      error: (err) => {
        console.log('[deliverable upload] error', err?.code, err?.message, err);
        this.uploadingByQuestion[questionIndex] = false;
        this.uploadErrorByQuestion[questionIndex] = err?.code === 'storage/unauthorized'
          ? "Vous n'avez pas l'autorisation d'uploader. Contactez l'équipe."
          : "L'upload a échoué. Réessayez ou contactez l'équipe.";
        input.value = '';
        this.cdRef.detectChanges();
      },
      complete: () => {
        storageRef.getDownloadURL().subscribe({
          next: (url) => {
            console.log('[deliverable upload] done', url);
            control.patchValue({ imageUrl: url });
            this.uploadingByQuestion[questionIndex] = false;
            input.value = '';
            this.cdRef.detectChanges();
          },
          error: (err) => {
            console.log('[deliverable upload] getDownloadURL error', err);
            this.uploadingByQuestion[questionIndex] = false;
            this.uploadErrorByQuestion[questionIndex] = "Impossible de récupérer le lien de l'image.";
            input.value = '';
            this.cdRef.detectChanges();
          }
        });
      }
    });
  }

  onModify(): void {
    this.isSubmitted = false;
    this.saveStatus = 'idle';
    this.deliverableForm.enable();
    this.autoSaveEnabled = true;
  }

  private disableForm(): void {
    this.deliverableForm.disable();
  }

  onNext(): void {
    this.goToNextCourse.emit();
  }

  goToRecap(): void {
    this.router.navigate(['/e-learning/masterclasses', this.masterclassId, 'deliverable-recap']);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
